import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useSelector } from 'react-redux';
import { I18nLookup } from 'pricing-pages-lib/utils/languageUtils';
import HubMap from 'self-service-api/constants/HubMap';
import { productLineToHub } from 'self-service-api/constants/ProductLineToHub';
import { API_LIMITED_ACCESS, API_NO_ACCESS } from 'self-service-api/constants/FreeUserAccessValues';
import * as PurchaseTypes from 'self-service-api/constants/PurchaseTypes';
import { CUSTOMER_FEEDBACK_SURVEYS, DEAL_TAGS, KNOWLEDGE_BASE, TICKET_TAGS } from 'self-service-api/constants/Features';
import styled from 'styled-components';
import { getProductLineFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import FeatureTableCell from './FeatureTableCell';
import TranslatedFeature from './TranslatedFeature';
import FeatureTableUserIcon from './FeatureTableUserIcon';
import { isUngatedForSeatsSelector } from '../../selectors/GatingSelectors';
import { useIsMobile } from '../../context/IsMobileContext';
const FeatureName = styled.div.attrs({
  tabIndex: -1
}).withConfig({
  displayName: "FeatureTableRow__FeatureName",
  componentId: "sc-6ef5yq-0"
})(["font-weight:600;"]);
const UserIconWrapper = styled.span.withConfig({
  displayName: "FeatureTableRow__UserIconWrapper",
  componentId: "sc-6ef5yq-1"
})(["width:2%;min-width:30px;"]);

const isSeatBased = productLine => {
  return HubMap[productLineToHub[productLine]].merchPurchaseType === PurchaseTypes.SEAT_BASED;
};

const seatsFreeUserAccessLevelOverrides = {
  [KNOWLEDGE_BASE]: API_NO_ACCESS,
  [CUSTOMER_FEEDBACK_SURVEYS]: API_NO_ACCESS,
  [DEAL_TAGS]: API_NO_ACCESS,
  [TICKET_TAGS]: API_NO_ACCESS
};

const FeatureTableRow = ({
  feature,
  highlightedProducts,
  productLine,
  onlyShowCurrentLongLimit = false
}) => {
  const isUngatedForSeats = useSelector(isUngatedForSeatsSelector);
  const isMobile = useIsMobile();
  const highestTierProduct = highlightedProducts[highlightedProducts.length - 1]; // This temporarily filters out features that we don't have in SSAPI.
  // There is some discrepancy between what we show currently and the features returned from the API.
  // This should be investigated and addressed as a fast follow

  const lookupVal = `pricing-pages.features.${feature.shortName}.name`;

  if (!I18nLookup(lookupVal)) {
    return null;
  }

  const accessLevel = isUngatedForSeats && seatsFreeUserAccessLevelOverrides[feature.shortName] || feature.freeUserAccessLevel;
  const showUserIcon = (productLine ? isSeatBased(productLine) : feature.productApiNames.some(apiName => {
    const localProductLine = getProductLineFromApiNameString(apiName);
    return isSeatBased(localProductLine);
  })) && (accessLevel === API_LIMITED_ACCESS || accessLevel === API_NO_ACCESS);
  return /*#__PURE__*/_jsxs("tr", {
    children: [/*#__PURE__*/_jsx("th", {
      scope: "row",
      children: /*#__PURE__*/_jsxs("div", {
        className: isMobile ? 'align-start' : 'align-center',
        children: [/*#__PURE__*/_jsx(UserIconWrapper, {
          children: showUserIcon && /*#__PURE__*/_jsx(FeatureTableUserIcon, {
            freeUserAccess: accessLevel,
            productLine: productLine
          })
        }), productLine ? /*#__PURE__*/_jsx("span", {
          style: {
            cursor: 'help'
          },
          children: /*#__PURE__*/_jsx(UITooltip, {
            use: "longform",
            title: /*#__PURE__*/_jsx(TranslatedFeature, {
              apiName: highestTierProduct,
              productLine: productLine,
              feature: feature,
              attribute: "description",
              needsProductLineIndexing: true
            }),
            children: /*#__PURE__*/_jsx(FeatureName, {
              children: /*#__PURE__*/_jsx(TranslatedFeature, {
                productLine: productLine,
                feature: feature,
                attribute: "name"
              })
            })
          })
        }) : /*#__PURE__*/_jsx(FeatureName, {
          children: /*#__PURE__*/_jsx(TranslatedFeature, {
            productLine: productLine,
            feature: feature,
            attribute: "name"
          })
        })]
      })
    }), highlightedProducts.map(apiName => {
      const localProductLine = getProductLineFromApiNameString(apiName);
      return /*#__PURE__*/_jsx(FeatureTableCell, {
        apiName: apiName,
        feature: feature,
        showTooltip: true,
        productLine: localProductLine,
        onlyShowCurrentLongLimit: onlyShowCurrentLongLimit
      }, apiName);
    })]
  }, feature.featureId);
};

export default FeatureTableRow;